import React, { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment'; // peer-dep of react-dates
import { SingleDatePicker } from 'react-dates';
import { ICON_AFTER_POSITION } from 'react-dates/constants';
import { Icon } from '@iq/react-components';

export const CALENDAR_ID = 'calendar-toggle';

// Date Picker typing requires React.Node (class)
class CalendarToggleIcon extends React.Component {
  render() {
    return (
      <Icon
        icon="he-calendar"
        size="s"
        onClick={this.props.onClick}
        id={this.props.id}
      />
    );
  }
}
export const getNextSelectionRange = (selectionStart, shift) => {
  if (shift) {
    if (selectionStart === 5) {
      return [0, 4];
    }
    if (selectionStart === 8) {
      return [5, 7];
    }
    return [8, 10];
  }
  if (selectionStart === 0) {
    return [5, 7];
  }
  if (selectionStart === 5) {
    return [8, 10];
  }
  return [0, 4];
};

export function pad(number, length) {
  return `${'0'.repeat(length - number.toString().length)}${number}`;
}

export function getDateString(y, m, d) {
  return `${pad(y, 4)}-${pad(m, 2)}-${pad(d, 2)}`;
}

const DatePicker = ({
  id,
  value: date,
  onChange,
  onCalendarOpen,
  onTextComplete,
  onTabOrArrowBack,
}) => {
  const [focused, _setFocused] = useState(false);
  const [focusedMonth, setFocusedMonth] = useState(null);
  const [localDate, _setLocalDate] = useState(date);
  const [temporaryYear, _setTemporaryYear] = useState(null);
  const [temporaryMonth, _setTemporaryMonth] = useState(null);
  const [temporaryDay, _setTemporaryDay] = useState(null);

  const inputReference = useRef(null);
  const calendarReference = useRef(null);
  const focusedReference = useRef(focused);
  const localDateReference = useRef(localDate);
  const temporaryYearReference = useRef(temporaryYear);
  const temporaryMonthReference = useRef(temporaryMonth);
  const temporaryDayReference = useRef(temporaryDay);

  const setFocused = (isFocused) => {
    focusedReference.current = isFocused;
    _setFocused(isFocused);
  };

  const setLocalDate = (d) => {
    localDateReference.current = d;
    _setLocalDate(d);
  };

  const setTemporaryYear = (year) => {
    temporaryYearReference.current = year;
    _setTemporaryYear(year);
  };

  const setTemporaryMonth = (month) => {
    temporaryMonthReference.current = month;
    _setTemporaryMonth(month);
  };

  const setTemporaryDay = (day) => {
    temporaryDayReference.current = day;
    _setTemporaryDay(day);
  };

  const refreshDate = useCallback((d) => {
    if (d) {
      setFocusedMonth(d);
      setLocalDate(d);
    } else {
      setLocalDate(d);
    }
  }, []);

  const handleOnChange = useCallback(
    (value) => {
      /*
       * selected value comes in at 00:00:00 so we have to re-add the initial time
       * so that timezone conversions work correctly
       */
      const update = new Date(localDateReference.current);

      if (value && moment(value).isValid()) {
        const newDate = new Date(value);
        update.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
      }

      refreshDate(update.getTime());
      return onChange(update.getTime());
    },
    [refreshDate, onChange]
  );

  useEffect(() => refreshDate(date), [date, refreshDate]);

  useEffect(() => {
    if (inputReference.current) {
      const calendarBtns = document.querySelectorAll('.SingleDatePickerInput_calendarIcon');
      for (const button of calendarBtns) {
        button.tabIndex = '-1';
      }
    }
  }, [inputReference]);

  const clearTemps = useCallback(() => {
    setTemporaryYear(null);
    setTemporaryMonth(null);
    setTemporaryDay(null);
  }, []);

  const onKeyDown = useCallback(
    (event) => {
      if (event.target && event.target.id === id) {
        const { selectionStart, selectionEnd, value: inputValue } = event.target;
        const [y, m, d] = inputValue.split('-');
        if (!focusedReference.current) {
          if (event.key === 'Tab' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            // tab/move can occur before entering full period (e.g. '0021', tempYear = '21'),
            // or after adjusting full period with arrow up/down,
            // or after clearing period with delete
            if (
              (event.key === 'Tab' &&
                !(event.shiftKey && selectionStart === 0 && !onTabOrArrowBack)) ||
              event.key === 'ArrowLeft' ||
              event.key === 'ArrowRight'
            ) {
              event.preventDefault();
            }
            let value;
            const shift = (event.key === 'Tab' && event.shiftKey) || event.key === 'ArrowLeft';
            const selectionRange = getNextSelectionRange(selectionStart, shift);

            switch (selectionStart) {
              case 0: {
                if (
                  Number.isNaN(Number.parseInt(y, 10)) ||
                  (temporaryYearReference.current &&
                    temporaryYearReference.current.value.length < 4)
                ) {
                  // if tab after delete (y = 'yyyy') or before completing input
                  value = moment(localDateReference.current).year().toString();
                  event.target.value = getDateString(value, m, d);
                  if (!shift) {
                    event.target.setSelectionRange(...selectionRange);
                  }
                  setTemporaryYear(null);
                } else if (
                  !shift &&
                  (!temporaryYearReference.current || !temporaryYearReference.current.value)
                ) {
                  event.target.setSelectionRange(...selectionRange);
                } else if (shift && onTabOrArrowBack) {
                  onTabOrArrowBack();
                }

                break;
              }
              case 5: {
                if (
                  Number.isNaN(Number.parseInt(m, 10)) ||
                  (temporaryMonthReference.current &&
                    temporaryMonthReference.current.value.length < 2)
                ) {
                  // if tab after delete (m = 'mm') or before completing input
                  value = moment(localDateReference.current).month() + 1;
                  event.target.value = getDateString(y, value, d);
                  event.target.setSelectionRange(...selectionRange);
                  setTemporaryMonth(null);
                } else if (
                  !temporaryMonthReference.current ||
                  !temporaryMonthReference.current.value
                ) {
                  event.target.setSelectionRange(...selectionRange);
                }

                break;
              }
              case 8: {
                if (
                  Number.isNaN(Number.parseInt(d, 10)) ||
                  (temporaryDayReference.current && temporaryDayReference.current.value.length < 2)
                ) {
                  // if tab after delete (d = 'dd') or before completing input
                  value = pad(moment(localDateReference.current).date(), 2);
                  event.target.value = getDateString(y, m, value);
                  if (shift) {
                    event.target.setSelectionRange(...selectionRange);
                  }
                  setTemporaryDay(null);
                } else if (
                  shift &&
                  (!temporaryDayReference.current || !temporaryDayReference.current.value)
                ) {
                  event.target.setSelectionRange(...selectionRange);
                } else if ((event.key === 'ArrowRight' || event.key === 'Tab') && onTextComplete) {
                  onTextComplete();
                }

                break;
              }
              default: {
                event.target.setSelectionRange(...selectionRange);
              }
            }
          }

          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            const [yI, mI, dI] = [y, m, d].map((v) => Number.parseInt(v, 10));
            let value;
            switch (selectionStart) {
              case 0: {
                value =
                  event.key === 'ArrowUp'
                    ? pad(yI < 9998 ? yI + 1 : '0000', 4)
                    : pad(yI > 1 ? yI - 1 : '9999', 4);
                setTemporaryYear({ value, retainFocus: true });

                break;
              }
              case 5: {
                value =
                  event.key === 'ArrowUp'
                    ? pad(mI < 12 ? mI + 1 : '01', 2)
                    : pad(mI > 1 ? mI - 1 : '12', 2);
                setTemporaryMonth({ value, retainFocus: true });

                break;
              }
              case 8: {
                const days = moment(`${y}-${m}`, 'YYYY-MM').daysInMonth();
                value =
                  event.key === 'ArrowUp'
                    ? pad(dI < (days || 27) ? dI + 1 : '01', 2)
                    : pad(dI > 1 ? dI - 1 : `${days || '28'}`, 2);
                setTemporaryDay({ value, retainFocus: true });

                break;
              }
              // No default
            }
          }

          if (inputValue && event.key === 'Backspace') {
            event.preventDefault();
            if (selectionStart === 0 && selectionEnd === 4) {
              event.target.value = `yyyy-${inputValue.slice(5)}`;
              event.target.setSelectionRange(0, 4);
              setTemporaryYear(null);
            } else if (selectionStart === 5 && selectionEnd === 7) {
              event.target.value = `${inputValue.slice(0, 5)}mm${inputValue.slice(7)}`;
              event.target.setSelectionRange(5, 7);
              setTemporaryMonth(null);
            } else if (selectionStart === 8 && selectionEnd === 10) {
              event.target.value = `${inputValue.slice(0, 8)}dd`;
              event.target.setSelectionRange(8, 10);
              setTemporaryDay(null);
            }
          }
        }

        if (
          focusedReference.current &&
          event.key === 'Tab' &&
          event.shiftKey &&
          selectionStart === 0
        ) {
          setFocused(false);
          clearTemps();
        }
      } else if (event.key === 'Tab' && document.activeElement.classList.contains('CalendarDay')) {
        setFocused(false);
        clearTemps();
      }
    },
    [id, onTabOrArrowBack, onTextComplete, clearTemps]
  );

  const onKeyPress = useCallback(
    (event) => {
      if (!focusedReference.current && event.target && event.target.id === id) {
        const { selectionStart, value: inputValue } = event.target;
        event.preventDefault();
        if (inputValue && Number.parseInt(event.key, 10) >= 0 && event.key >= 0 && event.key <= 9) {
          const [y, m, d] = inputValue.split('-');
          const days = moment(`${y}-${m}`, 'YYYY-MM').daysInMonth();
          let value = event.key;

          if (selectionStart === 0) {
            if (
              !temporaryYearReference.current ||
              (temporaryYearReference.current.value &&
                temporaryYearReference.current.value.length < 4)
            ) {
              value = `${(temporaryYearReference.current && temporaryYearReference.current.value) || ''}${event.key}`;
              event.target.value = getDateString(value, m, d);
            }
            setTemporaryYear({ value });
          }

          if (selectionStart === 5) {
            if (
              temporaryMonthReference.current &&
              temporaryMonthReference.current.value === '0' &&
              event.key === '0'
            ) {
              event.target.value = getDateString(y, event.key, d);
              event.target.setSelectionRange(5, 7);
              setTemporaryMonth({ value: '0' });
            } else {
              if (!temporaryMonthReference.current && event.key < 2) {
                event.target.value = getDateString(y, event.key, d);
                event.target.setSelectionRange(5, 7);
              } else if (
                (!temporaryMonthReference.current && event.key >= 2) ||
                (temporaryMonthReference.current &&
                  temporaryMonthReference.current.value === '0' &&
                  event.key > 0)
              ) {
                value = pad(event.key, 2);
              } else if (
                temporaryMonthReference.current &&
                temporaryMonthReference.current.value === '1' &&
                event.key < 3
              ) {
                value = `1${event.key}`;
              } else {
                value = '12';
              }
              setTemporaryMonth({ value });
            }
          }

          if (selectionStart === 8) {
            if (
              temporaryDayReference.current &&
              temporaryDayReference.current.value === '0' &&
              event.key === '0'
            ) {
              event.target.value = getDateString(y, m, event.key);
              event.target.setSelectionRange(8, 10);
              setTemporaryDay({ value: '0' });
            } else {
              if (
                (!temporaryDayReference.current && event.key === '0') ||
                (!temporaryDayReference.current &&
                  (event.key < 3 || (event.key === '3' && m !== '02')))
              ) {
                event.target.value = getDateString(y, m, event.key);
                event.target.setSelectionRange(8, 10);
              } else if (
                (!temporaryDayReference.current &&
                  (event.key > 3 || (event.key === '3' && m === '02'))) ||
                (temporaryDayReference.current.value === '0' && event.key > 0)
              ) {
                value = pad(event.key, 2);
              } else if (
                temporaryDayReference.current.value === '1' ||
                (temporaryDayReference.current.value === '2' && `2${event.key}` < days + 1) ||
                (temporaryDayReference.current.value === '3' && `3${event.key}` < days + 1)
              ) {
                value = `${temporaryDayReference.current.value}${event.key}`;
              } else {
                value = days.toString();
              }
              setTemporaryDay({ value });
            }
          }
        }
      } else if (focusedReference.current && event.target && event.target.id === id) {
        event.preventDefault();
      }
    },
    [id]
  );

  // only use localDate in deps array since we need update to occur
  // before setting selection and clearing temp values
  useEffect(() => {
    const dateInput = document.querySelector(`#${id}`);
    if (dateInput && localDate) {
      if (temporaryYear) {
        if (temporaryYear.value.length === 4) {
          if (temporaryYear.retainFocus) {
            dateInput.setSelectionRange(0, 4);
          } else if (!temporaryYear.shift) {
            dateInput.setSelectionRange(5, 7);
          }
          setTemporaryYear(null);
        } else {
          dateInput.setSelectionRange(0, 4);
        }
      }

      if (temporaryMonth) {
        if (temporaryMonth.value.length === 2) {
          if (temporaryMonth.retainFocus) {
            dateInput.setSelectionRange(5, 7);
          } else if (temporaryMonth.shift) {
            dateInput.setSelectionRange(0, 4);
          } else {
            dateInput.setSelectionRange(8, 10);
          }
          setTemporaryMonth(null);
        } else {
          dateInput.setSelectionRange(5, 7);
        }
      }

      if (temporaryDay) {
        if (temporaryDay.value.length === 2) {
          if (temporaryDay.retainFocus) {
            dateInput.setSelectionRange(8, 10);
          } else if (temporaryDay.shift) {
            dateInput.setSelectionRange(5, 7);
          } else if (onTextComplete) {
            onTextComplete();
          } else {
            dateInput.setSelectionRange(8, 10);
          }
          setTemporaryDay(null);
        } else {
          dateInput.setSelectionRange(8, 10);
        }
      }
    }
  }, [localDate, id, onTextComplete, temporaryDay, temporaryMonth, temporaryYear]);

  useEffect(() => {
    const dateInput = document.querySelector(`#${id}`);
    if (dateInput && temporaryYear && localDate) {
      const [, m, d] = dateInput.value.split('-');
      const ldm = moment(localDate);
      if (temporaryYear.value.length === 4 && temporaryYear.value !== ldm.year()) {
        handleOnChange(moment(getDateString(temporaryYear.value, m, d)));
      } else if (temporaryYear.value.length === 4 && temporaryYear.value === ldm.year()) {
        dateInput.value = getDateString(ldm.year(), ldm.month() + 1, ldm.date());
        dateInput.setSelectionRange(5, 7);
        setTemporaryYear(null);
      } else {
        dateInput.setSelectionRange(0, 4);
      }
    }
  }, [temporaryYear, localDate, handleOnChange, id]);

  useEffect(() => {
    const dateInput = document.querySelector(`#${id}`);

    if (dateInput && temporaryMonth && localDate) {
      const [y, , d] = dateInput.value.split('-');
      const ldm = moment(localDate);
      if (temporaryMonth.value.length === 2 && temporaryMonth.value !== pad(ldm.month() + 1, 2)) {
        handleOnChange(moment(getDateString(y, temporaryMonth.value, d)));
      } else if (
        temporaryMonth.value.length === 2 &&
        temporaryMonth.value === pad(ldm.month() + 1, 2) &&
        !temporaryMonth.retainFocus
      ) {
        dateInput.value = getDateString(ldm.year(), ldm.month() + 1, ldm.date());
        dateInput.setSelectionRange(8, 10);
        setTemporaryMonth(null);
      } else {
        dateInput.setSelectionRange(5, 7);
      }
    }
  }, [temporaryMonth, localDate, handleOnChange, id]);

  useEffect(() => {
    const dateInput = document.querySelector(`#${id}`);

    if (dateInput && temporaryDay && localDate) {
      const [y, m] = dateInput.value.split('-');
      const ldm = moment(localDate);
      if (temporaryDay.value.length === 2 && temporaryDay.value !== pad(ldm.date(), 2)) {
        handleOnChange(moment(getDateString(y, m, temporaryDay.value)));
      } else if (temporaryDay.value.length === 2 && temporaryDay.value === pad(ldm.date(), 2)) {
        dateInput.value = getDateString(ldm.year(), ldm.month() + 1, ldm.date());
        setTemporaryDay(null);
      } else {
        dateInput.setSelectionRange(8, 10);
      }
    }
  }, [temporaryDay, localDate, handleOnChange, id]);

  const onClick = useCallback(
    (event) => {
      if (!focusedReference.current && event.target && event.target.id === id) {
        const { selectionStart } = event.target;
        event.preventDefault();
        event.target.setSelectionRange(0, 0);
        if (selectionStart >= 0 && selectionStart <= 4) {
          event.target.setSelectionRange(0, 4);
        } else if (selectionStart >= 5 && selectionStart <= 7) {
          event.target.setSelectionRange(5, 7);
        } else if (selectionStart >= 8) {
          event.target.setSelectionRange(8, 10);
        }
      }

      const calendarIcon = document.querySelector(`#${id}-${CALENDAR_ID}`);
      if (calendarIcon && event.target === calendarIcon) {
        const dateInput = document.querySelector(`#${id}`);
        if (focusedReference.current) {
          event.preventDefault();
          event.stopPropagation();
          dateInput.blur();
        } else {
          dateInput.setSelectionRange(0, 0);
        }
      }

      if (focusedReference.current) {
        const picker = document.querySelectorAll(`#date-picker-${id} .DayPicker_focusRegion`);
        if (picker[0] && !picker[0].contains(event.target)) {
          setFocused(false);
        }
      }
    },
    [id]
  );

  const onFocus = useCallback(
    (event) => {
      if (event.target && event.target.id === id) {
        const { selectionStart, selectionEnd } = event.target;
        if (!focusedReference.current) {
          if (selectionStart === 0 && selectionEnd === 10) {
            event.preventDefault();
            event.target.setSelectionRange(0, 4);
          }
        } else if (selectionStart === 0 && selectionEnd === 10) {
          event.preventDefault();
          event.target.setSelectionRange(0, 0);
        }
      }
    },
    [id]
  );

  const onBlur = useCallback(
    (event) => {
      if (!focusedReference.current && event.target && event.target.id === id) {
        const { value } = event.target;
        const year = value.split('-')[0];
        const month = value.split('-')[1];
        const day = value.split('-')[2];
        const updated = new Date(localDateReference.current);
        if (updated.setYear) {
          updated.setYear(year);
          updated.setMonth(month - 1);
          updated.setDate(day);
        }
        handleOnChange(updated);
        clearTemps();
      }
    },
    [id, handleOnChange, clearTemps]
  );

  const onKeyUp = useCallback((event) => {
    if (focusedReference.current && event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setFocused(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keypress', onKeyPress, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('touchend', onClick, true);
    document.addEventListener('focus', onFocus, true);
    document.addEventListener('blur', onBlur, true);
    document.addEventListener('keyup', onKeyUp, true);

    if (onCalendarOpen) {
      onCalendarOpen(focused);
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keypress', onKeyPress, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('touchend', onClick, true);
      document.removeEventListener('focus', onFocus, true);
      document.removeEventListener('blur', onBlur, true);
      document.removeEventListener('keyup', onKeyUp, true);
    };
  }, [focused, onKeyDown, onKeyPress, onClick, onFocus, onBlur, onKeyUp, onCalendarOpen]);

  const handleChange = (selectedDate) => {
    handleOnChange(selectedDate);
    setFocused(false);
  };

  return (
    <div
      className="date-picker"
      id={`date-picker-${id}`}
      ref={inputReference}
    >
      <SingleDatePicker
        date={(localDate && moment(localDate)) || moment()}
        onDateChange={handleChange}
        focused={focused}
        onFocusChange={() => null}
        placeholder="yyyy-mm-dd"
        required
        showDefaultInputIcon={false}
        customInputIcon={
          <CalendarToggleIcon
            onClick={() => setFocused((isFocused) => !isFocused)}
            id={`${id}-${CALENDAR_ID}`}
            ref={calendarReference}
          />
        }
        inputIconPosition={ICON_AFTER_POSITION}
        keepOpenOnDateSelect={false}
        noBorder
        small
        initialVisibleMonth={() => (focusedMonth && moment(focusedMonth)) || moment()}
        hideKeyboardShortcutsPanel
        displayFormat="YYYY-MM-DD"
        numberOfMonths={1}
        isOutsideRange={() => false}
        id={id}
        //Rapid month navigation can interrupt the CSS transition so `transitionend` never fires, leaving react-dates stuck "animating"
        transitionDuration={0}
      />
    </div>
  );
};

export default DatePicker;
